/**
 * Settings panel — rendered inside the learn-side DOM.
 *
 * Contains:
 *   • Audio volume sliders (BGM + SFX)
 *   • Sync section: family-code display, last-synced time, sync-now button,
 *     reset-family-code button
 *   • Parent controls (👪): time-lock, parent-mode toggle, PIN setup/manage —
 *     all mutations gated by 4-digit parent PIN once one is set.
 *   • Privacy management section (§9.2-9.4 of PRIVACY.md) — also PIN-gated
 *     once a PIN exists. Without the gate, a curious kid tapping
 *     「🗑 完全刪除所有資料」 + typing 「刪除」 wipes everything → disaster.
 *
 * PIN flow:
 *   • No PIN set yet  → 設定家長 PIN button visible; clicking opens a modal
 *     to enter + confirm a 4-digit PIN, saved via saveProgress.
 *   • PIN set, locked → parent + privacy sections show 🔒 overlay; one
 *     unlock button opens the PIN entry modal. Correct PIN flips an in-
 *     memory unlock flag (this render only). 「忘記 PIN」 path → confirm,
 *     then clears PIN allowing user to set a fresh one.
 *   • PIN set, unlocked → all controls behave normally; 「上鎖」 button
 *     re-locks without leaving settings.
 *
 * Volume changes are immediately:
 *   1. Persisted to PlayerProgress (localStorage via saveProgress)
 *   2. Broadcast via CustomEvent `ef:audio-volume` so that BattleScene,
 *      learn-music and ui-sounds all pick up the new value without needing
 *      direct references to one another.
 */

import { loadProgress, saveProgress, createFreshProgress, setParentPin, verifyParentPin, hasParentPin } from '../shared/progress';
import type { DailyTimeLimitMinutes, GradeSemester, PlayerProgress } from '../shared/types';
import { uploadProgress, generateFamilyCode, isValidFamilyCode } from '../shared/sync-client';
import { openLegalModal } from './legal-modal';
import { createProfile, switchProfile } from '../shared/profile';
import { getDefaultAudioBus } from '../shared/audio-bus';
import { pageShell } from './ui/components';
import { goBack } from './nav/back-nav';
import { renderAccountSection } from './account-section';
import {
  GRADE_SEMESTER_OPTIONS,
  gradeSemesterLabel,
  gradeSemesterOf,
  setGradeSemester,
  nextGradeSemester,
  advanceGradeSemester,
} from './grade-semester';

/**
 * 學術風格設定頁的 scoped 樣式 —— 把舊夜空遊戲風(深藍底 / 青綠字 / 紅 material
 * 鈕)整批換成教科書紙感。頁殼 / 標題沿用設計系統(`pageShell` + `--ef-*`),
 * 此處只覆蓋設定頁特有的容器:section 卡、滑桿、勾選、下拉、PIN / 隱私彈窗,
 * 以及沿用的 legacy 按鈕 class(`.primary-btn` / `.secondary-btn` /
 * `.privacy-delete-btn`)。家長端要乾淨專業 → 單一學術綠重點色,無彩光。
 *
 * 為何不逐顆換成 academicButton:設定頁多顆鈕由 id + inline 行為驅動,且測試
 * 以 id 抓取;改用 class 統一外觀(token 化)風險最低、外觀仍一致。危險操作
 * (刪除全部)用紅描邊 danger 風格。冪等:固定 id 守衛只注入一份。
 */
const SETTINGS_STYLE_ID = 'ef-settings-style';
function ensureSettingsStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SETTINGS_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SETTINGS_STYLE_ID;
  style.textContent = `
.settings-screen { display: flex; flex-direction: column; gap: 18px; }

/* 區塊 = 紙片卡 */
.settings-screen .settings-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
  padding: 16px 18px 18px;
}
.settings-screen .settings-section h3 {
  margin: 0;
  font-family: var(--ef-serif);
  font-weight: 600;
  font-size: 17px;
  letter-spacing: 0.5px;
  color: var(--ef-ink);
  padding-left: 12px;
  border-left: 3px solid var(--ef-green);
}

/* 標籤列(滑桿 / 勾選 / 下拉) */
.settings-screen .slider-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--ef-ink-soft);
}
.settings-screen .slider-name { color: var(--ef-ink-soft); font-weight: 500; }
.settings-screen .slider-value { color: var(--ef-green); font-weight: 700; min-width: 2ch; text-align: right; }

/* 範圍滑桿:學術綠軌 */
.settings-screen .vol-slider {
  flex: 1;
  accent-color: var(--ef-green);
  cursor: pointer;
}
.settings-screen input[type="checkbox"] { accent-color: var(--ef-green); }

/* 下拉:紙底描邊 */
.settings-screen #daily-time-limit {
  font-size: 14px;
  padding: 6px 10px;
  background: var(--ef-paper);
  color: var(--ef-ink-soft);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
}

/* 同步碼 / 家庭碼顯示 */
.settings-screen #sync-code-display { color: var(--ef-ink); }
.settings-screen #sync-last-time { color: var(--ef-ink-soft); }

/* ── 沿用 legacy 按鈕 class,token 化成學術風格 ───────────────────── */
.settings-screen .primary-btn,
.settings-screen .secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  padding: 9px 20px;
  border-radius: var(--ef-radius-pill);
  font-family: var(--ef-sans);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  line-height: 1;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.08s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.settings-screen .primary-btn {
  background: var(--ef-green);
  color: #fff;
  box-shadow: 0 4px 12px rgba(31, 111, 92, 0.22);
}
.settings-screen .primary-btn:hover:not(:disabled) { background: var(--ef-green-dark); transform: translateY(-1px); }
.settings-screen .secondary-btn {
  background: transparent;
  color: var(--ef-green-dark);
  border-color: var(--ef-line);
}
.settings-screen .secondary-btn:hover:not(:disabled) { background: rgba(31, 111, 92, 0.06); border-color: var(--ef-green); }
.settings-screen .primary-btn:disabled,
.settings-screen .secondary-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.settings-screen button:focus-visible { outline: 3px solid rgba(31, 111, 92, 0.4); outline-offset: 2px; }

/* 危險:刪除全部 = 紅描邊 */
.settings-screen .privacy-delete-btn,
.settings-screen .privacy-delete-btn.secondary-btn {
  color: #b3261e;
  border-color: #e6c4c0;
  background: transparent;
}
.settings-screen .privacy-delete-btn:hover:not(:disabled) { background: rgba(179, 38, 30, 0.06); border-color: #b3261e; }

.settings-screen .legal-btn-row { display: flex; gap: 8px; flex-wrap: wrap; }

/* PIN / 隱私 banner —— 紙底提示條(取代舊深藍 #263238) */
.settings-screen #parent-lock-banner,
.settings-screen #parent-setup-banner,
.settings-screen #privacy-lock-banner {
  background: var(--ef-note);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
}

/* status 行 / 隱私說明:統一灰階 */
.settings-screen #sync-status-msg,
.settings-screen #pro-mode-status,
.settings-screen #reduce-motion-status,
.settings-screen #time-limit-status,
.settings-screen #parent-mode-status,
.settings-screen #experimental-mode-status,
.settings-screen #privacy-toast,
.settings-screen .privacy-section > p { color: var(--ef-sub); }

/* ── 彈窗(PIN setup / unlock / 隱私 reset / delete)紙感化 ───────── */
.privacy-modal-backdrop {
  position: fixed; inset: 0;
  display: none;
  align-items: center; justify-content: center;
  background: rgba(26, 26, 26, 0.45);
  z-index: 60;
  padding: 20px;
}
.privacy-modal {
  width: 100%;
  max-width: 360px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-lift);
  padding: 22px 22px 18px;
  color: var(--ef-ink-soft);
  font-family: var(--ef-sans);
}
.privacy-modal p { margin: 0 0 8px; line-height: 1.6; }
.privacy-modal .text-input {
  background: var(--ef-paper);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
  color: var(--ef-ink);
  padding: 8px 10px;
}
.privacy-modal-btns { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
`;
  document.head.appendChild(style);
}

/**
 * 教學專業模式查詢 — boss 2026-05-22 directive: 教學要專業 (不遊戲化、無音樂).
 *
 * Returns `true` when proMode is enabled. proMode defaults to ON when the
 * field is undefined (legacy / migrated saves) — opt-out semantics so the
 * professional answer screen is the default for everyone.
 *
 * Callers: `_runner.ts` (skip mascot/SFX), `learn-music.ts` (skip BGM),
 * `quiz/overlay.ts` (mute SFX + minimise banner), `exam/paper-view.ts`
 * (drop faction-XP framing + drama animation).
 */
export function isProMode(progress?: PlayerProgress | null): boolean {
  const p = progress ?? loadProgress();
  // Default ON when no progress yet (first launch before createFreshProgress).
  if (!p) return true;
  const v = p.parentSettings?.proMode;
  return v === undefined ? true : v;
}

/**
 * 減少晃動 (Reduce Motion) query — purely presentational accessibility flag.
 *
 * Returns `true` when reduce-motion is enabled. Defaults to FALSE (opt-IN) so
 * the existing hand-tuned screen-shake / hit-stop feel is preserved for everyone
 * until a parent turns the calmer mode on. Mirrors `isProMode` shape.
 *
 * Game-side does NOT call this directly (no src/game → src/learn import); the
 * battle scene reads `progress.parentSettings.reduceMotion` and pushes it into
 * `src/game/systems/motion-prefs.ts`. This helper is for learn-side UI / tests.
 */
export function isReduceMotion(progress?: PlayerProgress | null): boolean {
  const p = progress ?? loadProgress();
  if (!p) return false;
  return p.parentSettings?.reduceMotion === true;
}

function formatSyncTime(ts: number | undefined): string {
  if (!ts) return '從未同步';
  const d = new Date(ts);
  return d.toLocaleString('zh-TW', { dateStyle: 'short', timeStyle: 'short' });
}

const TIME_LIMIT_OPTIONS: { label: string; value: DailyTimeLimitMinutes }[] = [
  { label: '無限制', value: 0 },
  { label: '30 分鐘', value: 30 },
  { label: '45 分鐘', value: 45 },
  { label: '60 分鐘', value: 60 },
  { label: '90 分鐘', value: 90 },
];

/** A PIN attempt is "valid input" iff it is a 4-character string of decimal digits. */
function isValidPin(pin: string): boolean {
  return /^\d{4}$/.test(pin);
}

export function renderSettings(container: HTMLElement, onBack?: () => void): void {
  const progress = loadProgress() ?? createFreshProgress('指揮官');
  const masterPct = Math.round((progress.audio?.masterVolume ?? 1.0) * 100);
  const bgmPct = Math.round((progress.audio?.bgmVolume ?? 0.7) * 100);
  const sfxPct = Math.round((progress.audio?.sfxVolume ?? 0.8) * 100);
  const currentCode = progress.familyCode ?? '';
  const currentLimit: DailyTimeLimitMinutes = progress.parentSettings?.dailyTimeLimitMinutes ?? 0;
  const currentParentMode = progress.parentSettings?.parentMode === true;
  // v24: 教學專業模式 — 預設 ON (undefined ⇒ ON). 不 PIN-gate,因為這是學習體驗
  // 偏好不是管控設定 — 小孩自己想看遊戲化版本也應該能 toggle 回去。
  const currentProMode = isProMode(progress);
  // v25: 減少晃動 (reduce-motion) — 純呈現/無障礙旗標。預設 OFF (undefined ⇒ off)
  // 以保留現有手感;家長可主動開啟給易暈的小齡孩子。不 PIN-gate (體驗偏好非管控).
  const currentReduceMotion = isReduceMotion(progress);
  // task #49: 體驗模式 = experimentalMode 旗標。預設 undefined = MVP_MODE on (4 Phase 2
  // cards 隱藏)。家長 demo Phase 2 features 時可勾。
  const currentExperimentalMode = progress.parentSettings?.experimentalMode === true;
  // 學年綁定 (boss 2026-06-10):目前就讀學年(學期)。沒設 ⇒ 三下 3.2(grade-semester
  // 單一真相)。改變 ⇒ 出考卷中心範圍清單即時反映。不 PIN-gate(課程設定非危險管控)。
  const currentGrade: GradeSemester = gradeSemesterOf(progress);
  const canAdvanceGrade = nextGradeSemester(currentGrade) !== currentGrade;
  // PIN is set iff progress holds a hash-or-legacy value.
  const pinIsSet = hasParentPin(progress);
  // Per-render in-memory unlock flag. Closed over by event handlers so subsequent
  // gated actions inside the same render observe the unlock. Reset = next render.
  let parentUnlocked = !pinIsSet; // no PIN ⇒ always considered unlocked

  // 學術風格頁殼 + scoped 樣式。serif 主標「設定」由 pageShell 提供(故下方
  // 模板不再放 <h1>)。返回走統一 ‹ 返回(pageShell onBack)+ 集中式返回堆疊,
  // 左緣滑動共用;#settings-back 也走 goBack()。
  container.innerHTML = '';
  ensureSettingsStyle();
  const back = (): void => {
    if (onBack) onBack();
    else window.dispatchEvent(new CustomEvent('ef:return-home'));
  };
  const shell = pageShell(container, { title: '設定', onBack: back });

  shell.body.innerHTML = `
    <div class="settings-screen">

      <div class="settings-section">
        <h3>🔊 音量</h3>
        <label class="slider-label">
          <span class="slider-name">總音量</span>
          <input type="range" id="master-vol" class="vol-slider" min="0" max="100" value="${masterPct}" />
          <span id="master-val" class="slider-value">${masterPct}</span>%
        </label>
        <label class="slider-label">
          <span class="slider-name">背景音樂</span>
          <input type="range" id="bgm-vol" class="vol-slider" min="0" max="100" value="${bgmPct}" />
          <span id="bgm-val" class="slider-value">${bgmPct}</span>%
        </label>
        <label class="slider-label">
          <span class="slider-name">音效</span>
          <input type="range" id="sfx-vol" class="vol-slider" min="0" max="100" value="${sfxPct}" />
          <span id="sfx-val" class="slider-value">${sfxPct}</span>%
        </label>
      </div>

      <div class="settings-section">
        <h3>📚 教學介面</h3>
        <label class="slider-label" style="align-items:flex-start;gap:10px;">
          <input type="checkbox" id="pro-mode-toggle" ${currentProMode ? 'checked' : ''}
            style="width:20px;height:20px;cursor:pointer;margin-top:4px;" />
          <span style="flex:1;">
            <span class="slider-name" style="display:block;">教學專業模式</span>
            <span style="font-size:12px;color:var(--ef-sub);display:block;margin-top:2px;line-height:1.4;">
              開啟:答題畫面去除遊戲化元素 — 沒有背景音樂、答題音效、陪伴角色、修煉值彩光,
              文案以「分數 / 正確 / 錯誤」呈現。<br>
              關閉:還原原本的遊戲化體驗(修煉值動畫、五門派 emoji、陪伴精靈)。
            </span>
          </span>
        </label>
        <span id="pro-mode-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>
      </div>

      <div class="settings-section">
        <h3>🎓 目前年級學期</h3>
        <label class="slider-label" style="align-items:flex-start;gap:10px;">
          <span style="flex:1;">
            <span class="slider-name" style="display:block;">就讀年級學期</span>
            <span style="font-size:12px;color:var(--ef-sub);display:block;margin-top:2px;line-height:1.4;">
              決定「練 / 出考卷中心」預設只列哪個學年的課本單元;舊學年單元會留在「複習」。
              升上四年級後,改這裡即可換成四年級課程。
            </span>
          </span>
          <select id="grade-semester-select" style="font-size:14px;padding:6px 10px;background:var(--ef-paper);color:var(--ef-ink-soft);border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);">
            ${GRADE_SEMESTER_OPTIONS.map(opt =>
              `<option value="${opt.value}"${opt.value === currentGrade ? ' selected' : ''}>${opt.label}</option>`
            ).join('')}
          </select>
        </label>
        <button class="secondary-btn" id="grade-advance-btn"
          style="align-self:flex-start;${canAdvanceGrade ? '' : 'display:none;'}">
          ⬆️ 升上 ${gradeSemesterLabel(nextGradeSemester(currentGrade))}
        </button>
        <span id="grade-semester-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>
      </div>

      <div class="settings-section">
        <h3>🎮 遊戲體驗</h3>
        <label class="slider-label" style="align-items:flex-start;gap:10px;">
          <input type="checkbox" id="reduce-motion-toggle" ${currentReduceMotion ? 'checked' : ''}
            style="width:20px;height:20px;cursor:pointer;margin-top:4px;" />
          <span style="flex:1;">
            <span class="slider-name" style="display:block;">減少晃動</span>
            <span style="font-size:12px;color:var(--ef-sub);display:block;margin-top:2px;line-height:1.4;">
              開啟:戰鬥時大幅減弱畫面震動與停格,只留輕微打擊感 —
              適合容易暈、對畫面晃動敏感的孩子。<br>
              關閉:保留原本完整的打擊震動效果。
            </span>
          </span>
        </label>
        <span id="reduce-motion-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>
      </div>

      <div class="settings-section">
        <h3>☁️ 跨裝置同步</h3>
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span class="slider-name">家庭碼</span>
            <span id="sync-code-display" style="font-family: monospace; font-size: 1.2em; letter-spacing: 3px;">
              ${currentCode || '（未設定）'}
            </span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span class="slider-name">上次同步</span>
            <span id="sync-last-time">${formatSyncTime(progress.lastSyncedAt)}</span>
          </div>
          <span id="sync-status-msg" style="color: var(--ef-sub); min-height: 1.2em;"></span>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <button class="primary-btn" id="sync-now-btn" ${currentCode ? '' : 'disabled'}>立即同步</button>
            <button class="secondary-btn" id="set-family-code-btn">設定家庭碼</button>
            <button class="secondary-btn" id="reset-family-code-btn" ${currentCode ? '' : 'disabled'}
              style="${currentCode ? '' : 'opacity: 0.4;'}">重置家庭碼</button>
          </div>
        </div>
      </div>

      <!-- ☁️ 雲端帳號 —— renderAccountSection 注入到此 mount point。
           isCloudEnabled() 為 false 時不渲染任何節點(向後相容)。 -->
      <div id="account-section-mount"></div>

      <div class="settings-section" id="parent-controls-section">
        <h3>👪 家長管控</h3>

        <!-- PIN gate banner: visible when PIN is set & not yet unlocked this render -->
        <div id="parent-lock-banner" style="display:${pinIsSet && !parentUnlocked ? 'flex' : 'none'};
             align-items:center;gap:10px;padding:10px 12px;margin-bottom:10px;">
          <span style="font-size:1.4em;">🔒</span>
          <span style="flex:1;font-size:13px;color:var(--ef-ink-soft);">家長管控已上鎖,請輸入 4 位 PIN 解鎖</span>
          <button class="primary-btn" id="parent-unlock-btn">🔓 解鎖</button>
        </div>

        <!-- PIN setup banner: visible when PIN is NOT set -->
        <div id="parent-setup-banner" style="display:${pinIsSet ? 'none' : 'flex'};
             align-items:center;gap:10px;padding:10px 12px;margin-bottom:10px;">
          <span style="font-size:1.4em;">🛡️</span>
          <span style="flex:1;font-size:13px;color:var(--ef-ink-soft);">設定家長 PIN 保護管控設定(避免小孩誤觸)</span>
          <button class="primary-btn" id="parent-setup-pin-btn">設定家長 PIN</button>
        </div>

        <!-- Content area: hidden while locked, shown when unlocked OR no PIN -->
        <div id="parent-controls-body" style="display:${parentUnlocked ? 'flex' : 'none'};
             flex-direction:column;gap:8px;">
          <label class="slider-label">
            <span class="slider-name">每日遊戲時間上限</span>
            <select id="daily-time-limit">
              ${TIME_LIMIT_OPTIONS.map(opt =>
                `<option value="${opt.value}"${opt.value === currentLimit ? ' selected' : ''}>${opt.label}</option>`
              ).join('')}
            </select>
          </label>
          <span id="time-limit-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>

          <label class="slider-label" style="margin-top:8px;">
            <span class="slider-name">家長模式(修煉學院顯示量化進度)</span>
            <input type="checkbox" id="parent-mode-toggle" ${currentParentMode ? 'checked' : ''}
              style="width:20px;height:20px;cursor:pointer;" />
          </label>
          <span id="parent-mode-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>

          <label class="slider-label" style="margin-top:8px;">
            <span class="slider-name">🧪 體驗模式(顯示開發中功能:戰役 / 模擬考 / 無限魔王塔)</span>
            <input type="checkbox" id="experimental-mode-toggle" ${currentExperimentalMode ? 'checked' : ''}
              style="width:20px;height:20px;cursor:pointer;" />
          </label>
          <span id="experimental-mode-status" style="color:var(--ef-sub);min-height:1.2em;font-size:13px;"></span>

          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;">
            <button class="secondary-btn" id="parent-change-pin-btn"
              style="display:${pinIsSet ? '' : 'none'};">🔑 修改 PIN</button>
            <button class="secondary-btn" id="parent-relock-btn"
              style="display:${pinIsSet ? '' : 'none'};">🔒 重新上鎖</button>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <h3>🧑‍✈️ 指揮官管理</h3>
        <div style="display: flex; gap: 8px; flex-wrap: wrap;">
          <button class="secondary-btn" id="switch-profile-btn">🔄 切換指揮官</button>
          <button class="secondary-btn" id="add-profile-btn">+ 新增指揮官</button>
        </div>
      </div>

      <div class="settings-section">
        <h3>⚖️ 法律文件</h3>
        <div class="legal-btn-row">
          <button class="secondary-btn legal-doc-btn" id="btn-privacy">📄 隱私政策</button>
          <button class="secondary-btn legal-doc-btn" id="btn-terms">📜 使用條款</button>
        </div>
      </div>

      <div class="settings-section privacy-section">
        <h3>🔒 隱私管理</h3>
        <p style="font-size:13px;color:var(--ef-sub);margin:0 0 12px;">
          依台灣個資法，你可以隨時匯出、重置或刪除所有資料。
        </p>

        <!-- Privacy lock banner — mirrors parent-lock-banner but for the privacy section -->
        <div id="privacy-lock-banner" style="display:${pinIsSet && !parentUnlocked ? 'flex' : 'none'};
             align-items:center;gap:10px;padding:10px 12px;margin-bottom:10px;">
          <span style="font-size:1.4em;">🔒</span>
          <span style="flex:1;font-size:13px;color:var(--ef-ink-soft);">隱私管理已上鎖,請於上方解鎖家長區</span>
        </div>

        <div id="privacy-controls-body" style="display:${parentUnlocked ? 'flex' : 'none'};
             flex-direction:column;gap:10px;">
          <button class="secondary-btn" id="privacy-export-btn">📦 匯出我的全部資料</button>
          <button class="secondary-btn" id="privacy-reset-btn">🔄 重新開始遊戲（保留設定）</button>
          <button class="secondary-btn privacy-delete-btn" id="privacy-delete-btn">🗑 完全刪除所有資料</button>
        </div>
        <span id="privacy-toast" style="display:none;color:var(--ef-sub);font-size:13px;margin-top:8px;"></span>
      </div>

      <button class="secondary-btn" id="settings-back">← 返回</button>
    </div>

    <!-- ── PIN setup modal ──────────────────────────────────────────────── -->
    <div id="pin-setup-modal" class="privacy-modal-backdrop" style="display:none;">
      <div class="privacy-modal">
        <p style="font-weight:bold;">設定家長 PIN(4 位數字)</p>
        <p style="font-size:13px;color:var(--ef-sub);margin:4px 0 12px;">
          這組 PIN 用來保護家長管控與隱私管理,避免小孩誤觸。請選一組你記得住但孩子猜不到的數字。
        </p>
        <label style="font-size:13px;display:block;margin:8px 0 4px;">輸入 PIN:</label>
        <input type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4"
          id="pin-setup-input-1" class="text-input"
          style="width:120px;font-size:1.2em;letter-spacing:6px;text-align:center;" autocomplete="off" />
        <label style="font-size:13px;display:block;margin:8px 0 4px;">再次確認:</label>
        <input type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4"
          id="pin-setup-input-2" class="text-input"
          style="width:120px;font-size:1.2em;letter-spacing:6px;text-align:center;" autocomplete="off" />
        <span id="pin-setup-error" style="display:block;color:#b3261e;font-size:13px;min-height:1.2em;margin:6px 0 0;"></span>
        <div class="privacy-modal-btns" style="margin-top:12px;">
          <button class="secondary-btn" id="pin-setup-cancel">取消</button>
          <button class="primary-btn" id="pin-setup-confirm" disabled>儲存 PIN</button>
        </div>
      </div>
    </div>

    <!-- ── PIN unlock modal ─────────────────────────────────────────────── -->
    <div id="pin-unlock-modal" class="privacy-modal-backdrop" style="display:none;">
      <div class="privacy-modal">
        <p style="font-weight:bold;">輸入家長 PIN 解鎖</p>
        <input type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4"
          id="pin-unlock-input" class="text-input"
          style="width:120px;font-size:1.2em;letter-spacing:6px;text-align:center;margin-top:8px;" autocomplete="off" />
        <span id="pin-unlock-error" style="display:block;color:#b3261e;font-size:13px;min-height:1.2em;margin:6px 0 0;"></span>
        <div class="privacy-modal-btns" style="margin-top:12px;">
          <button class="secondary-btn" id="pin-unlock-forgot">忘記 PIN？</button>
          <button class="secondary-btn" id="pin-unlock-cancel">取消</button>
          <button class="primary-btn" id="pin-unlock-confirm" disabled>解鎖</button>
        </div>
      </div>
    </div>

    <!-- Privacy confirm modals (hidden by default) -->
    <div id="privacy-reset-modal" class="privacy-modal-backdrop" style="display:none;">
      <div class="privacy-modal">
        <p>確定要清除所有學習進度？設定會保留。<br><strong>此動作無法復原。</strong></p>
        <div class="privacy-modal-btns">
          <button class="secondary-btn" id="privacy-reset-cancel">取消</button>
          <button class="primary-btn" id="privacy-reset-confirm">確定重置</button>
        </div>
      </div>
    </div>

    <div id="privacy-delete-modal" class="privacy-modal-backdrop" style="display:none;">
      <div class="privacy-modal">
        <p style="color:#b3261e;font-weight:bold;">⚠️ 警告</p>
        <p>這會刪除所有資料（進度、設定、家庭碼）。<br><strong>動作無法復原。要繼續？</strong></p>
        <label style="font-size:13px;display:block;margin:8px 0 4px;">請打「刪除」確認：</label>
        <input type="text" id="privacy-delete-confirm-input" class="text-input"
          placeholder="刪除" style="width:120px;font-size:1em;" autocomplete="off" />
        <div class="privacy-modal-btns" style="margin-top:12px;">
          <button class="secondary-btn" id="privacy-delete-cancel">取消</button>
          <button class="primary-btn privacy-delete-btn" id="privacy-delete-confirm-btn" disabled>刪除全部</button>
        </div>
      </div>
    </div>
  `;

  // --- ☁️ 雲端帳號 section(Supabase 登入)---
  // isCloudEnabled() 為 false 時 renderAccountSection 不渲染任何節點(向後相容);
  // 設定頁此時看不到帳號區,行為跟雲端上線前 100% 一樣。
  const accountMount = shell.body.querySelector('#account-section-mount') as HTMLElement | null;
  if (accountMount) renderAccountSection(accountMount);

  // --- Audio ---
  // G08: route all three sliders through the shared AudioBuses singleton.
  // The bus owns persistence + the `ef:audio-volume` CustomEvent dispatch,
  // so existing listeners (ui-sounds.ts, learn-music.ts, BattleScene) keep
  // working — they read `.bgm`/`.sfx` which the bus now sets to the
  // post-master EFFECTIVE values (master × bus), so the master knob
  // affects them without code changes.
  const masterSlider = container.querySelector('#master-vol') as HTMLInputElement;
  const bgmSlider    = container.querySelector('#bgm-vol')    as HTMLInputElement;
  const sfxSlider    = container.querySelector('#sfx-vol')    as HTMLInputElement;
  const masterVal    = container.querySelector('#master-val') as HTMLElement;
  const bgmVal       = container.querySelector('#bgm-val')    as HTMLElement;
  const sfxVal       = container.querySelector('#sfx-val')    as HTMLElement;

  const bus = getDefaultAudioBus();

  function applyVolumes(): void {
    const master = Number(masterSlider.value) / 100;
    const music  = Number(bgmSlider.value)    / 100;
    const sfx    = Number(sfxSlider.value)    / 100;
    masterVal.textContent = masterSlider.value;
    bgmVal.textContent    = bgmSlider.value;
    sfxVal.textContent    = sfxSlider.value;

    // setAll persists to localStorage AND dispatches `ef:audio-volume`.
    bus.setAll({ master, music, sfx });
  }

  masterSlider.addEventListener('input', applyVolumes);
  bgmSlider.addEventListener('input', applyVolumes);
  sfxSlider.addEventListener('input', applyVolumes);

  // --- 📚 教學專業模式 toggle (v24) ---
  // 不 PIN-gate (學習偏好,非管控設定). 改變後 dispatch ef:pro-mode-changed,
  // learn-music.ts 監聽以便即時停掉 BGM (toggle 期間切換無需 reload).
  const proModeToggle = container.querySelector('#pro-mode-toggle') as HTMLInputElement | null;
  const proModeStatus = container.querySelector('#pro-mode-status') as HTMLElement | null;
  if (proModeToggle) {
    proModeToggle.addEventListener('change', () => {
      const enabled = proModeToggle.checked;
      const p = loadProgress() ?? createFreshProgress('指揮官');
      if (!p.parentSettings) {
        p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
      }
      p.parentSettings.proMode = enabled;
      saveProgress(p);
      if (proModeStatus) {
        proModeStatus.textContent = enabled
          ? '已啟用:答題畫面為專業模式(零遊戲化元素)'
          : '已關閉:答題畫面為遊戲化體驗';
      }
      try {
        window.dispatchEvent(new CustomEvent('ef:pro-mode-changed', { detail: { proMode: enabled } }));
      } catch { /* test harness */ }
    });
  }

  // --- 🎮 減少晃動 toggle (v25) ---
  // 純呈現/無障礙偏好,不 PIN-gate. 存進 parentSettings.reduceMotion;戰鬥場景
  // create() 時讀此旗標 push 進 motion-prefs,JuiceLayer 據此 clamp 震動/停格。
  const reduceMotionToggle = container.querySelector('#reduce-motion-toggle') as HTMLInputElement | null;
  const reduceMotionStatus = container.querySelector('#reduce-motion-status') as HTMLElement | null;
  if (reduceMotionToggle) {
    reduceMotionToggle.addEventListener('change', () => {
      const enabled = reduceMotionToggle.checked;
      const p = loadProgress() ?? createFreshProgress('指揮官');
      if (!p.parentSettings) {
        p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
      }
      p.parentSettings.reduceMotion = enabled;
      saveProgress(p);
      if (reduceMotionStatus) {
        reduceMotionStatus.textContent = enabled
          ? '已啟用:戰鬥震動大幅減弱(下一場戰鬥生效)'
          : '已關閉:戰鬥保留完整震動效果';
      }
      try {
        window.dispatchEvent(new CustomEvent('ef:reduce-motion-changed', { detail: { reduceMotion: enabled } }));
      } catch { /* test harness */ }
    });
  }

  // --- 🎓 目前年級學期 selector (boss 2026-06-10 學年綁定) ---
  // 走 grade-semester 單一真相寫入 currentGradeSemester(只切學年,不碰戰力 / 帳號)。
  // 持久化經 setGradeSemester → saveProgress(localStorage + 既有同步)。不 PIN-gate。
  const gradeSelect = container.querySelector('#grade-semester-select') as HTMLSelectElement | null;
  const gradeAdvanceBtn = container.querySelector('#grade-advance-btn') as HTMLButtonElement | null;
  const gradeStatus = container.querySelector('#grade-semester-status') as HTMLElement | null;
  /** 升級鈕文案 / 顯示依目前學年刷新(已到四下封頂則隱藏)。 */
  function refreshGradeAdvanceBtn(g: GradeSemester): void {
    if (!gradeAdvanceBtn) return;
    const next = nextGradeSemester(g);
    if (next === g) {
      gradeAdvanceBtn.style.display = 'none';
    } else {
      gradeAdvanceBtn.style.display = '';
      gradeAdvanceBtn.textContent = `⬆️ 升上 ${gradeSemesterLabel(next)}`;
    }
  }
  if (gradeSelect) {
    gradeSelect.addEventListener('change', () => {
      const g = Number(gradeSelect.value) as GradeSemester;
      setGradeSemester(g);
      if (gradeStatus) gradeStatus.textContent = `已設定:目前就讀 ${gradeSemesterLabel(g)}(出考卷中心已更新範圍)`;
      refreshGradeAdvanceBtn(g);
    });
  }
  if (gradeAdvanceBtn) {
    gradeAdvanceBtn.addEventListener('click', () => {
      const g = advanceGradeSemester();
      if (gradeSelect) gradeSelect.value = String(g);
      if (gradeStatus) gradeStatus.textContent = `已升上 ${gradeSemesterLabel(g)}(舊單元已留在「複習」)`;
      refreshGradeAdvanceBtn(g);
    });
  }

  // task #15: wire 「← 返回」 button so the settings page is escapable.
  // 改走 goBack() —— 與統一 ‹ / 左緣滑動同一路徑(走堆疊頂端 = 本頁 back 處理器)。
  const backBtn = container.querySelector('#settings-back') as HTMLButtonElement | null;
  if (backBtn) {
    backBtn.addEventListener('click', () => { goBack(); });
  }

  // --- Parent-section lock/unlock UI plumbing ---
  const parentLockBanner   = container.querySelector('#parent-lock-banner')   as HTMLElement;
  const parentSetupBanner  = container.querySelector('#parent-setup-banner')  as HTMLElement;
  const parentControlsBody = container.querySelector('#parent-controls-body') as HTMLElement;
  const privacyLockBanner  = container.querySelector('#privacy-lock-banner')  as HTMLElement;
  const privacyControlsBody = container.querySelector('#privacy-controls-body') as HTMLElement;
  const parentChangePinBtn = container.querySelector('#parent-change-pin-btn') as HTMLButtonElement;
  const parentRelockBtn    = container.querySelector('#parent-relock-btn')    as HTMLButtonElement;

  /** Sync the DOM to reflect the current parentUnlocked + PIN-set state. */
  function syncLockUI(): void {
    const p = loadProgress();
    const hasPin = !!p && hasParentPin(p);
    parentSetupBanner.style.display  = hasPin ? 'none' : 'flex';
    parentLockBanner.style.display   = hasPin && !parentUnlocked ? 'flex' : 'none';
    privacyLockBanner.style.display  = hasPin && !parentUnlocked ? 'flex' : 'none';
    parentControlsBody.style.display = parentUnlocked ? 'flex' : 'none';
    privacyControlsBody.style.display = parentUnlocked ? 'flex' : 'none';
    parentChangePinBtn.style.display = hasPin ? '' : 'none';
    parentRelockBtn.style.display    = hasPin ? '' : 'none';
  }

  // --- Parent controls (time limit + parent mode) — only wired once but mutations
  //     re-checked at click time via parentUnlocked. Because they live inside
  //     #parent-controls-body which is display:none while locked, they're also
  //     visually inert. ---
  const timeLimitSelect   = container.querySelector('#daily-time-limit')      as HTMLSelectElement;
  const timeLimitStatus   = container.querySelector('#time-limit-status')     as HTMLElement;
  const parentModeToggle  = container.querySelector('#parent-mode-toggle')    as HTMLInputElement;
  const parentModeStatus  = container.querySelector('#parent-mode-status')    as HTMLElement;
  const experimentalModeToggle = container.querySelector('#experimental-mode-toggle') as HTMLInputElement;
  const experimentalModeStatus = container.querySelector('#experimental-mode-status') as HTMLElement;
  timeLimitSelect.value = String(currentLimit);

  timeLimitSelect.addEventListener('change', () => {
    if (!parentUnlocked) return; // belt + braces — UI is also hidden when locked
    const val = Number(timeLimitSelect.value) as DailyTimeLimitMinutes;
    const p = loadProgress() ?? createFreshProgress('指揮官');
    if (!p.parentSettings) {
      p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
    }
    p.parentSettings.dailyTimeLimitMinutes = val;
    saveProgress(p);
    timeLimitStatus.textContent = val === 0 ? '已設定:無限制' : `已設定:每日上限 ${val} 分鐘`;
  });

  parentModeToggle.addEventListener('change', () => {
    if (!parentUnlocked) return;
    const enabled = parentModeToggle.checked;
    const p = loadProgress() ?? createFreshProgress('指揮官');
    if (!p.parentSettings) {
      p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
    }
    p.parentSettings.parentMode = enabled;
    saveProgress(p);
    parentModeStatus.textContent = enabled
      ? '已啟用:修煉學院顯示熟練度進度條'
      : '已停用:修煉學院顯示孩子模式';
  });

  // task #49: 體驗模式 toggle (MVP_MODE inverse) — controls 4 Phase 2 cards on home.
  experimentalModeToggle.addEventListener('change', () => {
    if (!parentUnlocked) return;
    const enabled = experimentalModeToggle.checked;
    const p = loadProgress() ?? createFreshProgress('指揮官');
    if (!p.parentSettings) {
      p.parentSettings = { dailyTimeLimitMinutes: 0, lastPlayDate: '', minutesPlayedToday: 0 };
    }
    p.parentSettings.experimentalMode = enabled;
    saveProgress(p);
    experimentalModeStatus.textContent = enabled
      ? '已啟用:home 顯示「戰役 / 模擬考 / 無限魔王塔 / 塔記錄板」(回 home 生效)'
      : '已停用:home 隱藏開發中功能(MVP 模式)';
  });

  // --- PIN setup modal ---
  const pinSetupModal   = container.querySelector('#pin-setup-modal')   as HTMLElement;
  const pinSetupInput1  = container.querySelector('#pin-setup-input-1') as HTMLInputElement;
  const pinSetupInput2  = container.querySelector('#pin-setup-input-2') as HTMLInputElement;
  const pinSetupError   = container.querySelector('#pin-setup-error')   as HTMLElement;
  const pinSetupConfirm = container.querySelector('#pin-setup-confirm') as HTMLButtonElement;
  const pinSetupCancel  = container.querySelector('#pin-setup-cancel')  as HTMLButtonElement;
  const parentSetupPinBtn = container.querySelector('#parent-setup-pin-btn') as HTMLButtonElement;

  function openPinSetup(): void {
    pinSetupInput1.value = '';
    pinSetupInput2.value = '';
    pinSetupError.textContent = '';
    pinSetupConfirm.disabled = true;
    pinSetupModal.style.display = 'flex';
  }
  function closePinSetup(): void {
    pinSetupModal.style.display = 'none';
  }
  function refreshSetupConfirm(): void {
    pinSetupConfirm.disabled = !(
      isValidPin(pinSetupInput1.value) && isValidPin(pinSetupInput2.value)
    );
  }
  pinSetupInput1.addEventListener('input', refreshSetupConfirm);
  pinSetupInput2.addEventListener('input', refreshSetupConfirm);
  parentSetupPinBtn.addEventListener('click', openPinSetup);
  parentChangePinBtn.addEventListener('click', openPinSetup); // re-uses the same flow
  pinSetupCancel.addEventListener('click', closePinSetup);
  pinSetupConfirm.addEventListener('click', () => {
    const a = pinSetupInput1.value;
    const b = pinSetupInput2.value;
    if (!isValidPin(a) || !isValidPin(b)) {
      pinSetupError.textContent = 'PIN 必須是 4 位數字';
      return;
    }
    if (a !== b) {
      pinSetupError.textContent = '兩次輸入不一致,請重新輸入';
      return;
    }
    const p = loadProgress() ?? createFreshProgress('指揮官');
    // Hash + persist asynchronously. Disable the button while in-flight so a
    // double-click can't race. Errors are surfaced to the inline error span.
    pinSetupConfirm.disabled = true;
    setParentPin(p, a)
      .then(() => {
        saveProgress(p);
        parentUnlocked = true; // freshly set PIN ⇒ also unlocked for this render
        syncLockUI();
        closePinSetup();
      })
      .catch((err: Error) => {
        pinSetupError.textContent = `PIN 設定失敗:${err.message}`;
        pinSetupConfirm.disabled = false;
      });
  });

  // --- PIN unlock modal ---
  const pinUnlockModal   = container.querySelector('#pin-unlock-modal')   as HTMLElement;
  const pinUnlockInput   = container.querySelector('#pin-unlock-input')   as HTMLInputElement;
  const pinUnlockError   = container.querySelector('#pin-unlock-error')   as HTMLElement;
  const pinUnlockConfirm = container.querySelector('#pin-unlock-confirm') as HTMLButtonElement;
  const pinUnlockCancel  = container.querySelector('#pin-unlock-cancel')  as HTMLButtonElement;
  const pinUnlockForgot  = container.querySelector('#pin-unlock-forgot')  as HTMLButtonElement;
  const parentUnlockBtn  = container.querySelector('#parent-unlock-btn')  as HTMLButtonElement;

  function openPinUnlock(): void {
    pinUnlockInput.value = '';
    pinUnlockError.textContent = '';
    pinUnlockConfirm.disabled = true;
    pinUnlockModal.style.display = 'flex';
  }
  function closePinUnlock(): void { pinUnlockModal.style.display = 'none'; }
  pinUnlockInput.addEventListener('input', () => {
    pinUnlockConfirm.disabled = !isValidPin(pinUnlockInput.value);
  });
  parentUnlockBtn.addEventListener('click', openPinUnlock);
  pinUnlockCancel.addEventListener('click', closePinUnlock);
  pinUnlockConfirm.addEventListener('click', () => {
    const entered = pinUnlockInput.value;
    const p = loadProgress();
    if (!p || !hasParentPin(p)) {
      // Edge case: PIN was cleared elsewhere — treat as unlocked.
      parentUnlocked = true;
      closePinUnlock();
      syncLockUI();
      return;
    }
    pinUnlockConfirm.disabled = true;
    verifyParentPin(p, entered).then((ok) => {
      if (!ok) {
        pinUnlockError.textContent = 'PIN 錯誤,請再試';
        pinUnlockConfirm.disabled = false;
        return;
      }
      parentUnlocked = true;
      closePinUnlock();
      syncLockUI();
    });
  });
  pinUnlockForgot.addEventListener('click', () => {
    // Forgot-PIN path: confirm clears the stored PIN. The parent must explicitly
    // walk through a confirm dialog (which a kid is unlikely to navigate
    // successfully). After reset, the user is back in "no PIN" state and can
    // either set a new one or leave it open.
    const ok = confirm('忘記 PIN?\n按「確定」會清除目前的 PIN,你之後可以重新設定一組新的。');
    if (!ok) return;
    const p = loadProgress();
    if (p?.parentSettings) {
      p.parentSettings.parentPin = undefined;
      saveProgress(p);
    }
    parentUnlocked = true;
    closePinUnlock();
    syncLockUI();
  });

  parentRelockBtn.addEventListener('click', () => {
    parentUnlocked = false;
    syncLockUI();
  });

  // --- Sync ---
  const syncStatusMsg  = container.querySelector('#sync-status-msg') as HTMLElement;
  const syncLastTime   = container.querySelector('#sync-last-time') as HTMLElement;
  const syncCodeDisplay = container.querySelector('#sync-code-display') as HTMLElement;
  const syncNowBtn     = container.querySelector('#sync-now-btn') as HTMLButtonElement;
  const resetCodeBtn   = container.querySelector('#reset-family-code-btn') as HTMLButtonElement;
  const setCodeBtn     = container.querySelector('#set-family-code-btn') as HTMLButtonElement;

  syncNowBtn.addEventListener('click', async () => {
    const p = loadProgress();
    if (!p?.familyCode) return;
    syncNowBtn.disabled = true;
    syncStatusMsg.style.color = 'var(--ef-green-dark)';
    syncStatusMsg.textContent = '同步中...';
    try {
      await uploadProgress(p.familyCode, p);
      p.lastSyncedAt = Date.now();
      saveProgress(p);
      syncLastTime.textContent = formatSyncTime(p.lastSyncedAt);
      syncStatusMsg.textContent = '同步成功';
    } catch (err) {
      syncStatusMsg.style.color = '#b3261e';
      syncStatusMsg.textContent = `同步失敗: ${(err as Error).message}`;
    } finally {
      syncNowBtn.disabled = false;
    }
  });

  setCodeBtn.addEventListener('click', () => {
    const newCode = prompt('請輸入 6 碼家庭碼(大寫英數字 A-Z, 2-9)\n留空自動產生新的:')?.trim().toUpperCase() ?? '';
    let code: string;
    if (newCode === '') {
      code = generateFamilyCode();
    } else if (isValidFamilyCode(newCode)) {
      code = newCode;
    } else {
      syncStatusMsg.style.color = '#b3261e';
      syncStatusMsg.textContent = '家庭碼格式錯誤,請輸入 6 個大寫英數字(A-Z, 2-9)';
      return;
    }
    const p = loadProgress() ?? createFreshProgress('指揮官');
    p.familyCode = code;
    saveProgress(p);
    syncCodeDisplay.textContent = code;
    syncNowBtn.disabled = false;
    resetCodeBtn.disabled = false;
    resetCodeBtn.style.opacity = '1';
    syncStatusMsg.style.color = 'var(--ef-green-dark)';
    syncStatusMsg.textContent = `家庭碼已設定為 ${code}`;
  });

  resetCodeBtn.addEventListener('click', () => {
    const p = loadProgress();
    if (!p?.familyCode) return;
    if (!confirm(`確定要重置家庭碼「${p.familyCode}」嗎?\n重置後舊的家庭碼將無法繼續使用。`)) return;
    p.familyCode = undefined;
    p.lastSyncedAt = undefined;
    saveProgress(p);
    syncCodeDisplay.textContent = '（未設定）';
    syncLastTime.textContent = formatSyncTime(undefined);
    syncNowBtn.disabled = true;
    resetCodeBtn.disabled = true;
    resetCodeBtn.style.opacity = '0.4';
    syncStatusMsg.style.color = 'var(--ef-green-dark)';
    syncStatusMsg.textContent = '家庭碼已重置';
  });

  // --- Profile management ---
  (container.querySelector('#switch-profile-btn') as HTMLButtonElement)
    .addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('ef:switch-profile'));
    });

  (container.querySelector('#add-profile-btn') as HTMLButtonElement)
    .addEventListener('click', () => {
      const nickname = prompt('新指揮官的名字:')?.trim();
      if (!nickname) return;
      const newProfile = createProfile(nickname);
      // Create fresh progress for the new profile immediately
      const fresh = createFreshProgress(nickname, newProfile.id);
      saveProgress(fresh, newProfile.id);
      // Switch to the new profile and go to the profile picker
      switchProfile(newProfile.id);
      window.dispatchEvent(new CustomEvent('ef:switch-profile'));
    });

  // --- Legal documents ---
  (container.querySelector('#btn-privacy') as HTMLButtonElement)
    .addEventListener('click', () => openLegalModal('privacy'));
  (container.querySelector('#btn-terms') as HTMLButtonElement)
    .addEventListener('click', () => openLegalModal('terms'));

  // --- Privacy management (PRIVACY.md §9.2-9.4) ---
  const privacyToast = container.querySelector('#privacy-toast') as HTMLElement;

  function showPrivacyToast(msg: string): void {
    privacyToast.textContent = msg;
    privacyToast.style.display = 'block';
    setTimeout(() => { privacyToast.style.display = 'none'; }, 4000);
  }

  // §9.2 Export all data
  (container.querySelector('#privacy-export-btn') as HTMLButtonElement)
    .addEventListener('click', () => {
      if (!parentUnlocked) return; // gate
      const allData: Record<string, unknown> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)!;
        try { allData[key] = JSON.parse(localStorage.getItem(key)!); }
        catch { allData[key] = localStorage.getItem(key); }
      }
      const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
      const today = new Date().toISOString().slice(0, 10);
      const filename = `eternal-fortress-export-${today}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      showPrivacyToast(`✅ 已匯出 ${filename}`);
    });

  // §9.3 Reset progress (keep parentSettings)
  const resetModal = container.querySelector('#privacy-reset-modal') as HTMLElement;
  (container.querySelector('#privacy-reset-btn') as HTMLButtonElement)
    .addEventListener('click', () => {
      if (!parentUnlocked) return;
      resetModal.style.display = 'flex';
    });
  (container.querySelector('#privacy-reset-cancel') as HTMLButtonElement)
    .addEventListener('click', () => { resetModal.style.display = 'none'; });
  (container.querySelector('#privacy-reset-confirm') as HTMLButtonElement)
    .addEventListener('click', () => {
      resetModal.style.display = 'none';
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)!;
        if (key.startsWith('eternal-fortress.progress')) keysToRemove.push(key);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      showPrivacyToast('✅ 進度已重置');
      setTimeout(() => { location.reload(); }, 1500);
    });

  // §9.4 Full delete
  const deleteModal = container.querySelector('#privacy-delete-modal') as HTMLElement;
  const deleteInput = container.querySelector('#privacy-delete-confirm-input') as HTMLInputElement;
  const deleteConfirmBtn = container.querySelector('#privacy-delete-confirm-btn') as HTMLButtonElement;

  (container.querySelector('#privacy-delete-btn') as HTMLButtonElement)
    .addEventListener('click', () => {
      if (!parentUnlocked) return;
      deleteInput.value = '';
      deleteConfirmBtn.disabled = true;
      deleteModal.style.display = 'flex';
    });
  (container.querySelector('#privacy-delete-cancel') as HTMLButtonElement)
    .addEventListener('click', () => { deleteModal.style.display = 'none'; });

  deleteInput.addEventListener('input', () => {
    deleteConfirmBtn.disabled = deleteInput.value !== '刪除';
  });

  deleteConfirmBtn.addEventListener('click', () => {
    if (deleteInput.value !== '刪除') return;
    deleteModal.style.display = 'none';
    localStorage.clear();
    showPrivacyToast('✅ 已完全刪除');
    setTimeout(() => { location.reload(); }, 1500);
  });
}
